import { ChangeEvent, PointerEvent, RefObject, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { uploadVideoToCos } from "../../api/cos";
import {
  getTeacherWebComparisonRunStatus,
  startTeacherWebComparisonRun,
  TeacherLinkInfo,
  TeacherStudentBinding,
  TeacherWebComparisonRun,
} from "../../api/teacherWeb";
import { preferredRecorderMimeType } from "../../utils/capabilities";
import { CropRect, exportOneSecondClip, TrimResult } from "../../utils/videoTrim";
import { clearUploadDraft, TeacherWebUploadDraft, UploadSourceKind } from "./uploadDraft";

type VideoMeta = {
  duration: number;
  height: number;
  width: number;
};

type UploadStatus = "idle" | "exporting" | "uploading" | "success" | "error";
type WorkflowStep = "select" | "frame" | "crop" | "action";
type CropDragMode = "move" | "nw" | "ne" | "sw" | "se";

type DragState = {
  cropRect: CropRect;
  mode: CropDragMode;
  pointerId: number;
  startX: number;
  startY: number;
};

type UploadSource = {
  blob: Blob;
  exportError: string | null;
  fileName: string;
  mimeType: string;
  sourceKind: UploadSourceKind;
};

type HandStrokeValue = "right_forehand" | "right_backhand" | "left_forehand" | "left_backhand";
type ActionValue = "drive" | "topspin" | "backspin" | "defense" | "push" | "chop";

const handStrokeOptions: Array<{ hand: string; label: string; stroke: string; value: HandStrokeValue }> = [
  { hand: "右手", label: "右手正手", stroke: "正手", value: "right_forehand" },
  { hand: "右手", label: "右手反手", stroke: "反手", value: "right_backhand" },
  { hand: "左手", label: "左手正手", stroke: "正手", value: "left_forehand" },
  { hand: "左手", label: "左手反手", stroke: "反手", value: "left_backhand" },
];

const actionOptions: Array<{ label: string; value: ActionValue }> = [
  { label: "攻球", value: "drive" },
  { label: "拉上旋球", value: "topspin" },
  { label: "拉下旋球", value: "backspin" },
  { label: "防守", value: "defense" },
  { label: "搓球", value: "push" },
  { label: "削球", value: "chop" },
];

const defaultHandStrokeValue: HandStrokeValue = "right_forehand";
const defaultActionValue: ActionValue = "drive";
const defaultTechType = `${defaultHandStrokeValue}_${defaultActionValue}`;

function buildTechType(handStrokeValue: HandStrokeValue, actionValue: ActionValue): string {
  return `${handStrokeValue}_${actionValue}`;
}

function splitTechType(techType: string): { actionValue: ActionValue; handStrokeValue: HandStrokeValue } {
  const handStrokeValue = handStrokeOptions.find((option) => techType.startsWith(`${option.value}_`))?.value ?? defaultHandStrokeValue;
  const actionValue = techType.slice(`${handStrokeValue}_`.length) as ActionValue;
  const validActionValue = actionOptions.some((option) => option.value === actionValue) ? actionValue : defaultActionValue;
  return { actionValue: validActionValue, handStrokeValue };
}

function getTechTypeLabel(techType: string): string {
  const { actionValue, handStrokeValue } = splitTechType(techType);
  const handStrokeLabel = handStrokeOptions.find((option) => option.value === handStrokeValue)?.label ?? "右手正手";
  const actionLabel = actionOptions.find((option) => option.value === actionValue)?.label ?? "攻球";
  return `${handStrokeLabel}${actionLabel}`;
}

const stepMeta: Record<WorkflowStep, { index: number; title: string; subtitle: string }> = {
  select: {
    index: 1,
    title: "选择动作视频",
    subtitle: "请选择 200 MB 以内、2 分钟以内、4K 以内的视频。",
  },
  frame: {
    index: 2,
    title: "选择击球时刻",
    subtitle: "拖动时间轴，停在要分析的那次动作的球拍触球那一帧。",
  },
  crop: {
    index: 3,
    title: "框选分析对象",
    subtitle: "拖动或缩放蓝框，确保只有分析对象在框内。",
  },
  action: {
    index: 4,
    title: "确认你的动作",
    subtitle: "请选择你的动作类型。",
  },
};

const defaultCrop: CropRect = {
  x: 0.12,
  y: 0.12,
  width: 0.76,
  height: 0.72,
};

const maxSourceVideoBytes = 200 * 1024 * 1024;
const maxSourceVideoDurationSec = 120;
const max4KLongEdge = 3840;
const max4KShortEdge = 2160;
const frameStepSec = 1 / 30;
const fakeAnalysisDurationMs = 40_000;
const analysisPollIntervalMs = 2_000;

export function VideoUploadWorkflow({
  binding,
  link,
  onLogout,
  token,
}: {
  binding: TeacherStudentBinding;
  link: TeacherLinkInfo;
  onLogout: () => void;
  token: string;
}) {
  const navigate = useNavigate();
  const existingVideoInputRef = useRef<HTMLInputElement | null>(null);
  const captureInputRef = useRef<HTMLInputElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<DragState | null>(null);
  const analysisAbortControllerRef = useRef<AbortController | null>(null);
  const frameAutoplayUrlRef = useRef("");

  const [draft, setDraft] = useState<TeacherWebUploadDraft | null>(null);
  const [step, setStep] = useState<WorkflowStep>("select");
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoMeta, setVideoMeta] = useState<VideoMeta | null>(null);
  const [cropRect, setCropRect] = useState<CropRect>(defaultCrop);
  const [cropPreviewTime, setCropPreviewTime] = useState(0);
  const [isCropPreviewPlaying, setIsCropPreviewPlaying] = useState(false);
  const [isFramePreviewPlaying, setIsFramePreviewPlaying] = useState(false);
  const [hitFrameTime, setHitFrameTime] = useState(0);
  const [startTimeSec, setStartTimeSec] = useState(0);
  const [trimResult, setTrimResult] = useState<TrimResult | null>(null);
  const [status, setStatus] = useState<UploadStatus>("idle");
  const [message, setMessage] = useState("请选择动作视频。");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [isWaitingForAnalysis, setIsWaitingForAnalysis] = useState(false);
  const [selectedTechType, setSelectedTechType] = useState(defaultTechType);
  const [run, setRun] = useState<TeacherWebComparisonRun | null>(null);
  const [runMessage, setRunMessage] = useState("");
  const [isStartingRun, setIsStartingRun] = useState(false);
  const [showFullFrameConfirm, setShowFullFrameConfirm] = useState(false);

  const recorderMime = useMemo(() => preferredRecorderMimeType(), []);
  const isAndroid = useMemo(() => /Android/i.test(navigator.userAgent), []);
  const existingVideoAccept = isAndroid ? undefined : "video/*";
  const currentStep = stepMeta[step];
  const canContinueFromFrame = Boolean(videoFile && videoMeta);
  const canSubmit = Boolean((draft || (videoFile && videoMeta)) && selectedTechType && status !== "exporting" && status !== "uploading" && !isStartingRun);
  const selectedTechParts = useMemo(() => splitTechType(selectedTechType), [selectedTechType]);
  const selectedTechLabel = getTechTypeLabel(selectedTechType);
  const studentName = binding.studentName?.trim() || "当前学生";

  useEffect(() => {
    clearUploadDraft(token, binding.studentUserId);
  }, [binding.studentUserId, token]);

  useEffect(() => {
    return () => {
      analysisAbortControllerRef.current?.abort();
      if (videoUrl) URL.revokeObjectURL(videoUrl);
    };
  }, [videoUrl]);

  useEffect(() => {
    return () => {
      if (trimResult?.url) URL.revokeObjectURL(trimResult.url);
    };
  }, [trimResult?.url]);

  function handleFileSelected(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    applySelectedFile(file);
  }

  function applySelectedFile(file: File | null) {
    if (!file) {
      setMessage("未选择视频");
      return;
    }

    if (!isVideoFile(file)) {
      setStatus("error");
      setMessage("请选择视频文件");
      return;
    }

    if (file.size > maxSourceVideoBytes) {
      clearSelectedVideo({ keepStep: false });
      setStatus("error");
      setMessage(`视频不能超过 200 MB，当前 ${formatBytes(file.size)}。`);
      return;
    }

    clearSelectedVideo({ keepStep: true });
    setVideoFile(file);
    setVideoUrl(URL.createObjectURL(file));
    setStep("frame");
    setStatus("idle");
    setMessage(`已选择 ${file.name}`);
  }

  function handleVideoLoaded() {
    const video = videoRef.current;
    if (!video) return;

    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    const nextMeta = {
      duration,
      height: video.videoHeight,
      width: video.videoWidth,
    };
    const validationMessage = validateVideoMeta(nextMeta);
    if (validationMessage) {
      clearSelectedVideo({ keepStep: false });
      setStatus("error");
      setMessage(validationMessage);
      return;
    }

    let nextHitFrame: number | null = null;
    setVideoMeta((current) => {
      if (!current) {
        nextHitFrame = 0;
        setHitFrameTime(nextHitFrame);
        setStartTimeSec(clipStartFromHitFrame(nextHitFrame, duration));
      }
      return nextMeta;
    });

    if (step === "frame") {
      const targetTime = nextHitFrame ?? clamp(hitFrameTime, 0, Math.max(0, duration - 0.05));
      window.setTimeout(() => seekFrameVideo(targetTime), 0);
      window.setTimeout(() => seekFrameVideo(targetTime), 160);
    }
  }

  function clearSelectedVideo({ keepStep }: { keepStep: boolean }) {
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    if (trimResult?.url) URL.revokeObjectURL(trimResult.url);
    clearUploadDraft(token, binding.studentUserId);
    setVideoFile(null);
    setVideoUrl("");
    setVideoMeta(null);
    setTrimResult(null);
    setDraft(null);
    setRun(null);
    setRunMessage("");
    setUploadProgress(0);
    setAnalysisProgress(0);
    setIsWaitingForAnalysis(false);
    analysisAbortControllerRef.current?.abort();
    analysisAbortControllerRef.current = null;
    setCropPreviewTime(0);
    setIsCropPreviewPlaying(false);
    setIsFramePreviewPlaying(false);
    setHitFrameTime(0);
    setStartTimeSec(0);
    setCropRect(defaultCrop);
    dragStateRef.current = null;
    frameAutoplayUrlRef.current = "";
    if (!keepStep) setStep("select");
  }

  function invalidatePreparedClip() {
    if (trimResult?.url) URL.revokeObjectURL(trimResult.url);
    clearUploadDraft(token, binding.studentUserId);
    setTrimResult(null);
    setDraft(null);
    setRun(null);
    setRunMessage("");
    setUploadProgress(0);
    setAnalysisProgress(0);
    setIsWaitingForAnalysis(false);
    analysisAbortControllerRef.current?.abort();
    analysisAbortControllerRef.current = null;
    if (status === "success") setStatus("idle");
  }

  function updateHitFrameTime(nextTime: number) {
    if (!videoMeta) return;
    const clamped = clamp(nextTime, 0, Math.max(0, videoMeta.duration - 0.05));
    setIsFramePreviewPlaying(false);
    setHitFrameTime(clamped);
    setStartTimeSec(clipStartFromHitFrame(clamped, videoMeta.duration));
    if (step === "frame") seekFrameVideo(clamped);
    invalidatePreparedClip();
  }

  function syncHitFrameFromVideo(nextTime: number) {
    if (!videoMeta) return;
    const clamped = clamp(nextTime, 0, Math.max(0, videoMeta.duration - 0.05));
    setHitFrameTime(clamped);
    setStartTimeSec(clipStartFromHitFrame(clamped, videoMeta.duration));
    invalidatePreparedClip();
  }

  function adjustHitFrame(frameDelta: number) {
    updateHitFrameTime(hitFrameTime + frameDelta * frameStepSec);
  }

  function updateCropRect(nextRect: CropRect) {
    setCropRect(clampCrop(nextRect));
    invalidatePreparedClip();
  }

  function seekPreview(time: number) {
    const video = videoRef.current;
    if (!video) return;
    try {
      video.pause();
      video.currentTime = time;
    } catch {
      // Some embedded browsers may reject a seek while the media element is remounting.
    }
  }

  function seekFrameVideo(time: number) {
    const video = videoRef.current;
    if (!video) return;
    try {
      video.pause();
      video.currentTime = time;
    } catch {
      // Some embedded browsers may reject a seek while the media element is remounting.
    }
  }

  function goToCropStep() {
    setIsFramePreviewPlaying(false);
    if (!videoMeta) {
      setStatus("error");
      setMessage("视频尚未加载完成");
      return;
    }
    const nextStartTime = clipStartFromHitFrame(hitFrameTime, videoMeta.duration);
    setStartTimeSec(nextStartTime);
    setCropPreviewTime(hitFrameTime);
    setStep("crop");
    window.setTimeout(() => seekPreview(hitFrameTime), 0);
  }

  useEffect(() => {
    if (step !== "frame" || !videoMeta || !videoUrl) return;
    const shouldAutoplay = frameAutoplayUrlRef.current !== videoUrl;
    if (shouldAutoplay) frameAutoplayUrlRef.current = videoUrl;

    const targetTime = clamp(hitFrameTime, 0, Math.max(0, videoMeta.duration - 0.05));
    const timers = [0, 160].map((delay) => window.setTimeout(() => seekFrameVideo(targetTime), delay));
    if (shouldAutoplay) {
      timers.push(window.setTimeout(() => {
        void playFramePreview();
      }, 260));
    }

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [step, videoMeta, videoUrl]);

  useEffect(() => {
    if (!isFramePreviewPlaying || step !== "frame" || !videoMeta) return;

    const timer = window.setInterval(() => {
      const video = videoRef.current;
      if (!video) return;
      if (video.ended || video.currentTime >= videoMeta.duration - 0.03) {
        video.pause();
        setIsFramePreviewPlaying(false);
      }
    }, 80);

    return () => window.clearInterval(timer);
  }, [isFramePreviewPlaying, step, videoMeta]);

  async function toggleFramePreviewPlayback() {
    if (!videoMeta) return;
    const video = videoRef.current;
    if (!video) return;

    try {
      if (isFramePreviewPlaying) {
        video.pause();
        setIsFramePreviewPlaying(false);
        return;
      }

      await playFramePreview();
    } catch {
      setIsFramePreviewPlaying(false);
    }
  }

  async function playFramePreview() {
    if (!videoMeta) return;
    const video = videoRef.current;
    if (!video) return;

    try {
      if (video.currentTime >= videoMeta.duration - 0.03 || video.ended) {
        video.currentTime = 0;
      }
      video.muted = true;
      video.playsInline = true;
      await video.play();
      setIsFramePreviewPlaying(true);
    } catch {
      setIsFramePreviewPlaying(false);
    }
  }

  useEffect(() => {
    if (!isCropPreviewPlaying || (step !== "crop" && step !== "action") || !videoMeta) return;

    const timer = window.setInterval(() => {
      const video = videoRef.current;
      if (!video) return;
      const clipEndTime = Math.min(videoMeta.duration, startTimeSec + 1);
      if (video.currentTime >= clipEndTime - 0.03 || video.ended) {
        video.currentTime = startTimeSec;
        void video.play().catch(() => setIsCropPreviewPlaying(false));
      }
    }, 80);

    return () => window.clearInterval(timer);
  }, [isCropPreviewPlaying, startTimeSec, step, videoMeta]);

  function updateCropPreviewTime(nextTime: number) {
    if (!videoMeta) return;
    const clipEndTime = Math.min(videoMeta.duration, startTimeSec + 1);
    const clamped = clamp(nextTime, startTimeSec, clipEndTime);
    setIsCropPreviewPlaying(false);
    setCropPreviewTime(clamped);
    seekPreview(clamped);
  }

  function syncCropPreviewFromVideo(nextTime: number) {
    if (!videoMeta) return;
    const clipEndTime = Math.min(videoMeta.duration, startTimeSec + 1);
    const clamped = clamp(nextTime, startTimeSec, clipEndTime);
    setCropPreviewTime(clamped);
  }

  async function toggleCropPreviewPlayback() {
    if (!videoMeta) return;
    const video = videoRef.current;
    if (!video) return;

    const clipEndTime = Math.min(videoMeta.duration, startTimeSec + 1);
    try {
      if (isCropPreviewPlaying) {
        video.pause();
        setIsCropPreviewPlaying(false);
        return;
      }

      if (video.currentTime < startTimeSec || video.currentTime >= clipEndTime - 0.03) {
        video.currentTime = startTimeSec;
      }
      video.muted = true;
      video.playsInline = true;
      await video.play();
      setIsCropPreviewPlaying(true);
    } catch {
      setIsCropPreviewPlaying(false);
    }
  }

  function handleReselectVideo() {
    clearSelectedVideo({ keepStep: false });
    setStatus("idle");
    setMessage("请重新选择动作视频。");
  }

  function goToActionStep() {
    setIsCropPreviewPlaying(false);
    if (isFullFrame(cropRect)) {
      setShowFullFrameConfirm(true);
      return;
    }
    setCropPreviewTime(startTimeSec);
    setStep("action");
    window.setTimeout(() => seekPreview(startTimeSec), 0);
  }

  function updateSelectedHandStroke(handStrokeValue: HandStrokeValue) {
    setSelectedTechType(buildTechType(handStrokeValue, selectedTechParts.actionValue));
  }

  function updateSelectedAction(actionValue: ActionValue) {
    setSelectedTechType(buildTechType(selectedTechParts.handStrokeValue, actionValue));
  }

  async function handleUploadAndStartAnalysis() {
    if (!selectedTechType) {
      setRunMessage("请选择动作类型");
      return;
    }

    try {
      setIsStartingRun(true);
      setRun(null);
      setRunMessage("");
      const readyDraft = draft ?? await uploadCurrentSelection();
      const created = await createAnalysisRun(readyDraft);
      setRun(created);
      await waitForAnalysisCompletion(created.runId);
    } catch (error) {
      setRun(null);
      setRunMessage(error instanceof Error ? error.message : "AI教练任务创建失败");
    } finally {
      setIsStartingRun(false);
    }
  }

  async function waitForAnalysisCompletion(runId: string): Promise<void> {
    analysisAbortControllerRef.current?.abort();
    const controller = new AbortController();
    analysisAbortControllerRef.current = controller;

    const startedAt = Date.now();
    setAnalysisProgress(0);
    setIsWaitingForAnalysis(true);
    setRunMessage("AI教练正在分析...");

    const progressTimer = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const nextProgress = Math.min(99, Math.floor((elapsed / fakeAnalysisDurationMs) * 99));
      setAnalysisProgress(nextProgress);
    }, 250);

    try {
      while (!controller.signal.aborted) {
        const status = await getTeacherWebComparisonRunStatus(runId, controller.signal);
        if (status.status === "comp completed") {
          setAnalysisProgress(100);
          setRunMessage("分析完成，正在打开结果。");
          await delay(350);
          if (controller.signal.aborted) return;
          navigate(`/r/${runId}`);
          return;
        }

        if (status.status === "comp failed") {
          throw new Error(status.error_message || "AI教练分析失败，请重试");
        }

        await delay(analysisPollIntervalMs);
      }
    } finally {
      window.clearInterval(progressTimer);
      setIsWaitingForAnalysis(false);
      if (analysisAbortControllerRef.current === controller) {
        analysisAbortControllerRef.current = null;
      }
    }
  }

  async function uploadCurrentSelection(): Promise<TeacherWebUploadDraft> {
    if (!videoFile || !videoMeta) {
      throw new Error("请先选择视频");
    }

    setStatus("exporting");
    setUploadProgress(0);
    setMessage("正在导出 1 秒片段...");

    const source = await prepareUploadSource();
    setStatus("uploading");
    setMessage("正在上传 1 秒片段...");

    const uploaded = await uploadVideoToCos({
      blob: source.blob,
      fileName: source.fileName,
      mimeType: source.mimeType,
      onProgress: setUploadProgress,
      sourceKind: source.sourceKind,
      teacherToken: token,
    });

    const nextDraft: TeacherWebUploadDraft = {
      bucket: uploaded.bucket,
      cdnDomain: uploaded.cdnDomain,
      cropRect,
      durationSec: 1,
      exportError: source.exportError,
      fileSize: source.blob.size,
      linkId: binding.linkId,
      mimeType: source.mimeType,
      objectKey: uploaded.objectKey,
      originalFileName: videoFile.name,
      originalFileSize: videoFile.size,
      originalMimeType: videoFile.type || inferMimeFromFileName(videoFile.name),
      originalVideoDurationSec: videoMeta.duration,
      originalVideoHeight: videoMeta.height,
      originalVideoWidth: videoMeta.width,
      region: uploaded.region,
      sourceKind: source.sourceKind,
      startTimeSec,
      studentUserId: binding.studentUserId,
      teacherToken: token,
      teacherUserId: binding.teacherUserId,
      uploadedAt: new Date().toISOString(),
    };

    setDraft(nextDraft);
    setStatus("success");
    setMessage("上传完成，正在创建分析任务。");
    return nextDraft;
  }

  async function createAnalysisRun(readyDraft: TeacherWebUploadDraft): Promise<TeacherWebComparisonRun> {
    return startTeacherWebComparisonRun({
      cropRect: readyDraft.cropRect,
      durationSec: readyDraft.sourceKind === "original_fallback" ? readyDraft.originalVideoDurationSec : readyDraft.durationSec,
      environment: {
        userAgent: navigator.userAgent,
      },
      exportError: readyDraft.exportError,
      fileSize: readyDraft.fileSize,
      mimeType: readyDraft.mimeType,
      objectKey: readyDraft.objectKey,
      originalFileName: readyDraft.originalFileName,
      originalFileSize: readyDraft.originalFileSize,
      originalMimeType: readyDraft.originalMimeType,
      originalVideoDurationSec: readyDraft.originalVideoDurationSec,
      sourceKind: readyDraft.sourceKind,
      startTimeSec: readyDraft.startTimeSec,
      teacherToken: token,
      techType: selectedTechType,
      videoHeight: readyDraft.originalVideoHeight,
      videoWidth: readyDraft.originalVideoWidth,
    });
  }

  async function prepareUploadSource(): Promise<UploadSource> {
    if (!videoFile || !videoRef.current) {
      throw new Error("请先选择视频");
    }

    if (trimResult) {
      return {
        blob: trimResult.blob,
        exportError: null,
        fileName: buildUploadFileName(videoFile.name, trimResult.mimeType),
        mimeType: trimResult.mimeType || trimResult.blob.type || "video/webm",
        sourceKind: "trimmed",
      };
    }

    try {
      const result = await createTrimResult();
      setTrimResult((current) => {
        if (current?.url) URL.revokeObjectURL(current.url);
        return result;
      });
      return {
        blob: result.blob,
        exportError: null,
        fileName: buildUploadFileName(videoFile.name, result.mimeType),
        mimeType: result.mimeType || result.blob.type || "video/webm",
        sourceKind: "trimmed",
      };
    } catch (error) {
      const exportError = error instanceof Error ? error.message : "前端导出失败";
      throw new Error(`当前设备没有成功导出 1 秒片段：${exportError}`);
    }
  }

  async function createTrimResult(): Promise<TrimResult> {
    if (!videoRef.current) {
      throw new Error("视频尚未加载");
    }
    if (!recorderMime) {
      throw new Error("当前 WebView 没有可用导出格式");
    }

    return exportOneSecondClip({
      cropRect,
      mimeType: recorderMime,
      startTime: startTimeSec,
      video: videoRef.current,
    });
  }

  function handleCropPointerDown(event: PointerEvent<HTMLElement>, mode: CropDragMode) {
    event.preventDefault();
    event.stopPropagation();
    dragStateRef.current = {
      cropRect,
      mode,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function handleCropPointerMove(event: PointerEvent<HTMLDivElement>) {
    const dragState = dragStateRef.current;
    const stage = stageRef.current;
    if (!dragState || !stage || dragState.pointerId !== event.pointerId) {
      return;
    }

    const rect = stage.getBoundingClientRect();
    const deltaX = (event.clientX - dragState.startX) / Math.max(1, rect.width);
    const deltaY = (event.clientY - dragState.startY) / Math.max(1, rect.height);
    updateCropRect(resizeCrop(dragState.cropRect, dragState.mode, deltaX, deltaY));
  }

  function handleCropPointerUp(event: PointerEvent<HTMLDivElement>) {
    if (dragStateRef.current?.pointerId === event.pointerId) {
      dragStateRef.current = null;
    }
  }

  return (
    <section className="upload-wizard">
      <input
        ref={existingVideoInputRef}
        accept={existingVideoAccept}
        className="hidden-file-input"
        type="file"
        onChange={handleFileSelected}
        onClick={(event) => {
          event.currentTarget.value = "";
        }}
      />
      <input
        ref={captureInputRef}
        accept="video/*"
        capture="environment"
        className="hidden-file-input"
        type="file"
        onChange={handleFileSelected}
        onClick={(event) => {
          event.currentTarget.value = "";
        }}
      />

      <header className="upload-wizard-header">
        <div className="upload-wizard-brand-row">
          <div className="student-login-brand" aria-label="乒派">
            <img src="/pingpi-logo.png" alt="" aria-hidden="true" />
            <span>乒派</span>
          </div>
          <button className="upload-logout-button" type="button" aria-label="退出登录" title="退出登录" onClick={onLogout}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M9 6.75V5.5A2.5 2.5 0 0 1 11.5 3h5A2.5 2.5 0 0 1 19 5.5v13a2.5 2.5 0 0 1-2.5 2.5h-5A2.5 2.5 0 0 1 9 18.5v-1.25" />
              <path d="M4 12h10" />
              <path d="m11 8 4 4-4 4" />
            </svg>
          </button>
        </div>
        <div className="upload-wizard-kicker">
          <span>{currentStep.index} / 4</span>
          <strong>{studentName} · {classTitle(link)}</strong>
        </div>
        <h2>{currentStep.title}</h2>
        <p>{currentStep.subtitle}</p>
        <div className="upload-step-track" aria-label="上传步骤">
          {(["select", "frame", "crop", "action"] as WorkflowStep[]).map((item) => (
            <span
              className={stepMeta[item].index <= currentStep.index ? "active" : ""}
              key={item}
            />
          ))}
        </div>
      </header>

      {step === "select" ? (
        <SelectVideoStep
          message={message}
          onCapture={() => captureInputRef.current?.click()}
          onPickExisting={() => existingVideoInputRef.current?.click()}
          status={status}
        />
      ) : null}

      {step === "frame" ? (
        <FrameStep
          hitFrameTime={hitFrameTime}
          message={message}
          onAdjustFrame={adjustHitFrame}
          onNext={goToCropStep}
          onReselectVideo={handleReselectVideo}
          onTimeChange={updateHitFrameTime}
          onVideoTimeChange={syncHitFrameFromVideo}
          onTogglePlay={toggleFramePreviewPlayback}
          status={status}
          videoMeta={videoMeta}
          videoRef={videoRef}
          videoUrl={videoUrl}
          onVideoLoaded={handleVideoLoaded}
          canContinue={canContinueFromFrame}
          isPlaying={isFramePreviewPlaying}
        />
      ) : null}

      {step === "crop" ? (
        <CropStep
          cropRect={cropRect}
          cropPreviewTime={cropPreviewTime}
          isPlaying={isCropPreviewPlaying}
          message={message}
          onBack={() => {
            setIsCropPreviewPlaying(false);
            setStep("frame");
          }}
          onCropPointerDown={handleCropPointerDown}
          onCropPointerMove={handleCropPointerMove}
          onCropPointerUp={handleCropPointerUp}
          onCropPreviewTimeChange={updateCropPreviewTime}
          onCropVideoTimeChange={syncCropPreviewFromVideo}
          onNext={goToActionStep}
          onTogglePlay={toggleCropPreviewPlayback}
          stageRef={stageRef}
          startTimeSec={startTimeSec}
          status={status}
          videoMeta={videoMeta}
          videoRef={videoRef}
          videoUrl={videoUrl}
          onVideoLoaded={handleVideoLoaded}
        />
      ) : null}

      {step === "action" ? (
        <ActionStep
          canSubmit={canSubmit}
          draft={draft}
          analysisProgress={analysisProgress}
          isWaitingForAnalysis={isWaitingForAnalysis}
          isStartingRun={isStartingRun}
          isPreviewPlaying={isCropPreviewPlaying}
          message={message}
          onActionChange={updateSelectedAction}
          onBack={() => {
            setIsCropPreviewPlaying(false);
            setStep(videoFile ? "crop" : "select");
          }}
          onHandStrokeChange={updateSelectedHandStroke}
          onPreviewTimeChange={updateCropPreviewTime}
          onPreviewVideoTimeChange={syncCropPreviewFromVideo}
          onStart={handleUploadAndStartAnalysis}
          onTogglePreviewPlay={toggleCropPreviewPlayback}
          runMessage={runMessage}
          selectedActionValue={selectedTechParts.actionValue}
          selectedHandStrokeValue={selectedTechParts.handStrokeValue}
          selectedTechLabel={selectedTechLabel}
          startTimeSec={startTimeSec}
          status={status}
          trimResult={trimResult}
          uploadProgress={uploadProgress}
          cropPreviewTime={cropPreviewTime}
          videoMeta={videoMeta}
          videoRef={videoRef}
          videoUrl={videoUrl}
          onVideoLoaded={handleVideoLoaded}
        />
      ) : null}

      {showFullFrameConfirm ? (
        <div className="student-logout-dialog-backdrop" role="presentation" onClick={() => setShowFullFrameConfirm(false)}>
          <div
            aria-labelledby="full-frame-title"
            aria-modal="true"
            className="student-logout-dialog"
            role="dialog"
            onClick={(event) => event.stopPropagation()}
          >
            <div>
              <h3 id="full-frame-title">使用全画面？</h3>
              <p>如果视频中出现多人，未框选分析对象可能导致分析失败。确认继续吗？</p>
            </div>
            <div className="student-logout-actions">
              <button className="student-logout-secondary" type="button" onClick={() => setShowFullFrameConfirm(false)}>
                返回框选
              </button>
              <button
                className="student-logout-primary"
                type="button"
                onClick={() => {
                  setShowFullFrameConfirm(false);
                  setCropPreviewTime(startTimeSec);
                  setStep("action");
                  window.setTimeout(() => seekPreview(startTimeSec), 0);
                }}
              >
                继续
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function SelectVideoStep({
  message,
  onCapture,
  onPickExisting,
  status,
}: {
  message: string;
  onCapture: () => void;
  onPickExisting: () => void;
  status: UploadStatus;
}) {
  return (
    <div className="upload-step-panel upload-select-step">
      <div className="upload-source-grid">
        <button className="upload-source-button" type="button" onClick={onPickExisting}>
          <span>选择已有视频</span>
          <small>从相册或文件中选择</small>
        </button>
        <button className="upload-source-button" type="button" onClick={onCapture}>
          <span>拍摄视频</span>
          <small>直接调用相机录制</small>
        </button>
      </div>
      <StatusMessage message={message} status={status} />
    </div>
  );
}

function FrameStep({
  canContinue,
  hitFrameTime,
  isPlaying,
  message,
  onAdjustFrame,
  onNext,
  onReselectVideo,
  onTimeChange,
  onTogglePlay,
  onVideoTimeChange,
  onVideoLoaded,
  status,
  videoMeta,
  videoRef,
  videoUrl,
}: {
  canContinue: boolean;
  hitFrameTime: number;
  isPlaying: boolean;
  message: string;
  onAdjustFrame: (frameDelta: number) => void;
  onNext: () => void;
  onReselectVideo: () => void;
  onTimeChange: (time: number) => void;
  onTogglePlay: () => void;
  onVideoTimeChange: (time: number) => void;
  onVideoLoaded: () => void;
  status: UploadStatus;
  videoMeta: VideoMeta | null;
  videoRef: RefObject<HTMLVideoElement | null>;
  videoUrl: string;
}) {
  return (
    <div className="upload-step-panel">
      <VideoStage
        allowProgrammaticPlayback
        showControls={false}
        videoMeta={videoMeta}
        videoRef={videoRef}
        videoUrl={videoUrl}
        onVideoLoaded={onVideoLoaded}
        onVideoTimeChange={onVideoTimeChange}
      />
      <div className="frame-control-card">
        <div className="frame-time-row">
          <span>当前帧</span>
          <strong>{formatClock(hitFrameTime)}</strong>
        </div>
        <div className="crop-scrub-row">
          <button className="crop-play-button" type="button" onClick={onTogglePlay} aria-label={isPlaying ? "暂停视频" : "播放视频"}>
            {isPlaying ? (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M8 5v14" />
                <path d="M16 5v14" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M8 5.5v13l10-6.5-10-6.5Z" />
              </svg>
            )}
          </button>
          <input
            aria-label="选择击球时刻"
            className="frame-timeline"
            disabled={!videoMeta}
            max={Math.max(0, videoMeta?.duration ?? 0)}
            min={0}
            step={0.01}
            type="range"
            value={hitFrameTime}
            onChange={(event) => onTimeChange(Number(event.target.value))}
          />
        </div>
        <div className="frame-time-scale">
          <span>0:00.00</span>
          <span>{videoMeta ? formatClock(videoMeta.duration) : "--"}</span>
        </div>
        <div className="frame-adjust-grid" aria-label="逐帧微调">
          {[-30, -5, -1, 1, 5, 30].map((frames) => (
            <button key={frames} disabled={!videoMeta} type="button" onClick={() => onAdjustFrame(frames)}>
              {frames > 0 ? `+${frames}` : frames}
            </button>
          ))}
        </div>
      </div>
      {status === "error" ? <StatusMessage message={message} status={status} /> : null}
      <div className="upload-step-actions two-buttons">
        <button className="ghost-button" type="button" onClick={onReselectVideo}>
          重新选择视频
        </button>
        <button className="primary-button" disabled={!canContinue} type="button" onClick={onNext}>
          下一步
        </button>
      </div>
    </div>
  );
}

function CropStep({
  cropRect,
  cropPreviewTime,
  isPlaying,
  message,
  onBack,
  onCropPointerDown,
  onCropPointerMove,
  onCropPointerUp,
  onCropPreviewTimeChange,
  onCropVideoTimeChange,
  onNext,
  onTogglePlay,
  onVideoLoaded,
  stageRef,
  startTimeSec,
  status,
  videoMeta,
  videoRef,
  videoUrl,
}: {
  cropRect: CropRect;
  cropPreviewTime: number;
  isPlaying: boolean;
  message: string;
  onBack: () => void;
  onCropPointerDown: (event: PointerEvent<HTMLElement>, mode: CropDragMode) => void;
  onCropPointerMove: (event: PointerEvent<HTMLDivElement>) => void;
  onCropPointerUp: (event: PointerEvent<HTMLDivElement>) => void;
  onCropPreviewTimeChange: (time: number) => void;
  onCropVideoTimeChange: (time: number) => void;
  onNext: () => void;
  onTogglePlay: () => void;
  onVideoLoaded: () => void;
  stageRef: RefObject<HTMLDivElement | null>;
  startTimeSec: number;
  status: UploadStatus;
  videoMeta: VideoMeta | null;
  videoRef: RefObject<HTMLVideoElement | null>;
  videoUrl: string;
}) {
  const clipEndTime = Math.min(videoMeta?.duration ?? startTimeSec + 1, startTimeSec + 1);

  return (
    <div className="upload-step-panel">
      <VideoStage
        cropRect={cropRect}
        cropOverlay
        allowProgrammaticPlayback
        showControls={false}
        stageRef={stageRef}
        videoMeta={videoMeta}
        videoRef={videoRef}
        videoUrl={videoUrl}
        onCropPointerDown={onCropPointerDown}
        onCropPointerMove={onCropPointerMove}
        onCropPointerUp={onCropPointerUp}
        onVideoLoaded={onVideoLoaded}
        onVideoTimeChange={onCropVideoTimeChange}
      />
      <div className="crop-scrub-row">
        <button className="crop-play-button" type="button" onClick={onTogglePlay} aria-label={isPlaying ? "暂停预览" : "播放预览"}>
          {isPlaying ? (
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M8 5v14" />
              <path d="M16 5v14" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M8 5.5v13l10-6.5-10-6.5Z" />
            </svg>
          )}
        </button>
        <input
          aria-label="查看 1 秒动作片段"
          className="frame-timeline"
          disabled={!videoMeta}
          max={clipEndTime}
          min={startTimeSec}
          step={0.01}
          type="range"
          value={cropPreviewTime}
          onChange={(event) => onCropPreviewTimeChange(Number(event.target.value))}
        />
      </div>
      <p className="crop-warning">如果视频里有多人，请务必只框住要分析的对象。</p>
      {status === "error" ? <StatusMessage message={message} status={status} /> : null}
      <div className="upload-step-actions two-buttons">
        <button className="ghost-button" type="button" onClick={onBack}>
          上一步
        </button>
        <button className="primary-button" type="button" onClick={onNext}>
          下一步
        </button>
      </div>
    </div>
  );
}

function ActionStep({
  analysisProgress,
  canSubmit,
  cropPreviewTime,
  draft,
  isWaitingForAnalysis,
  isStartingRun,
  isPreviewPlaying,
  message,
  onActionChange,
  onBack,
  onHandStrokeChange,
  onPreviewTimeChange,
  onPreviewVideoTimeChange,
  onStart,
  onTogglePreviewPlay,
  onVideoLoaded,
  runMessage,
  selectedActionValue,
  selectedHandStrokeValue,
  selectedTechLabel,
  startTimeSec,
  status,
  trimResult,
  uploadProgress,
  videoMeta,
  videoRef,
  videoUrl,
}: {
  analysisProgress: number;
  canSubmit: boolean;
  cropPreviewTime: number;
  draft: TeacherWebUploadDraft | null;
  isWaitingForAnalysis: boolean;
  isStartingRun: boolean;
  isPreviewPlaying: boolean;
  message: string;
  onActionChange: (actionValue: ActionValue) => void;
  onBack: () => void;
  onHandStrokeChange: (handStrokeValue: HandStrokeValue) => void;
  onPreviewTimeChange: (time: number) => void;
  onPreviewVideoTimeChange: (time: number) => void;
  onStart: () => void;
  onTogglePreviewPlay: () => void;
  onVideoLoaded: () => void;
  runMessage: string;
  selectedActionValue: ActionValue;
  selectedHandStrokeValue: HandStrokeValue;
  selectedTechLabel: string;
  startTimeSec: number;
  status: UploadStatus;
  trimResult: TrimResult | null;
  uploadProgress: number;
  videoMeta: VideoMeta | null;
  videoRef: RefObject<HTMLVideoElement | null>;
  videoUrl: string;
}) {
  const isBusy = status === "exporting" || status === "uploading" || isStartingRun;
  const progressValue = isWaitingForAnalysis ? analysisProgress : uploadProgress;
  const shouldShowProgress = isWaitingForAnalysis || status === "uploading";
  const clipEndTime = Math.min(videoMeta?.duration ?? startTimeSec + 1, startTimeSec + 1);
  const actionStatusMessage = runMessage || (status === "error" ? message : "");

  return (
    <div className="upload-step-panel">
      {videoUrl ? (
        <div className="action-preview-section">
          <VideoStage
            compact
            allowProgrammaticPlayback
            showControls={false}
            videoMeta={videoMeta}
            videoRef={videoRef}
            videoUrl={videoUrl}
            onVideoLoaded={onVideoLoaded}
            onVideoTimeChange={onPreviewVideoTimeChange}
          />
          <div className="crop-scrub-row action-scrub-row">
            <button className="crop-play-button" type="button" onClick={onTogglePreviewPlay} aria-label={isPreviewPlaying ? "暂停预览" : "播放预览"}>
              {isPreviewPlaying ? (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M8 5v14" />
                  <path d="M16 5v14" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M8 5.5v13l10-6.5-10-6.5Z" />
                </svg>
              )}
            </button>
            <input
              aria-label="查看 1 秒动作片段"
              className="frame-timeline"
              disabled={!videoMeta}
              max={clipEndTime}
              min={startTimeSec}
              step={0.01}
              type="range"
              value={clamp(cropPreviewTime, startTimeSec, clipEndTime)}
              onChange={(event) => onPreviewTimeChange(Number(event.target.value))}
            />
          </div>
        </div>
      ) : null}

      <section className="action-choice-section" aria-labelledby="hand-stroke-title">
        <div className="action-choice-header">
          <h3 id="hand-stroke-title">动作信息</h3>
          <span>{handStrokeOptions.find((option) => option.value === selectedHandStrokeValue)?.label}</span>
        </div>
        <div className="hand-stroke-grid" role="radiogroup" aria-label="选择左右手和正反手">
          {handStrokeOptions.map((option) => (
            <button
              aria-checked={option.value === selectedHandStrokeValue}
              className={`action-choice-button ${option.value === selectedHandStrokeValue ? "selected" : ""}`}
              key={option.value}
              role="radio"
              type="button"
              onClick={() => onHandStrokeChange(option.value)}
            >
              <span>{option.hand} · {option.stroke}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="action-choice-section" aria-labelledby="action-kind-title">
        <div className="action-choice-header">
          <h3 id="action-kind-title">动作类型</h3>
          <span>{selectedTechLabel}</span>
        </div>
        <div className="action-type-grid" role="radiogroup" aria-label="选择动作类型">
          {actionOptions.map((option) => (
            <button
              aria-checked={option.value === selectedActionValue}
              className={`action-choice-button ${option.value === selectedActionValue ? "selected" : ""}`}
              key={option.value}
              role="radio"
              type="button"
              onClick={() => onActionChange(option.value)}
            >
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      </section>

      {shouldShowProgress ? (
        <div className="upload-progress">
          <span style={{ width: `${progressValue}%` }} />
        </div>
      ) : null}

      <StatusMessage message={actionStatusMessage} status={status} />
      <div className="upload-step-actions two-buttons">
        <button className="ghost-button" disabled={isBusy} type="button" onClick={onBack}>
          上一步
        </button>
        <button className="primary-button" disabled={!canSubmit} type="button" onClick={onStart}>
          {isBusy ? statusLabel(status, isStartingRun) : "开始分析"}
        </button>
      </div>
    </div>
  );
}

function VideoStage({
  allowProgrammaticPlayback = false,
  compact = false,
  cropOverlay = false,
  cropRect,
  onCropPointerDown,
  onCropPointerMove,
  onCropPointerUp,
  onVideoLoaded,
  onVideoTimeChange,
  showControls = true,
  stageRef,
  videoMeta,
  videoRef,
  videoUrl,
}: {
  allowProgrammaticPlayback?: boolean;
  compact?: boolean;
  cropOverlay?: boolean;
  cropRect?: CropRect;
  onCropPointerDown?: (event: PointerEvent<HTMLElement>, mode: CropDragMode) => void;
  onCropPointerMove?: (event: PointerEvent<HTMLDivElement>) => void;
  onCropPointerUp?: (event: PointerEvent<HTMLDivElement>) => void;
  onVideoLoaded: () => void;
  onVideoTimeChange?: (time: number) => void;
  showControls?: boolean;
  stageRef?: RefObject<HTMLDivElement | null>;
  videoMeta: VideoMeta | null;
  videoRef: RefObject<HTMLVideoElement | null>;
  videoUrl: string;
}) {
  const style = videoMeta ? { aspectRatio: `${videoMeta.width} / ${videoMeta.height}` } : undefined;

  return (
    <div
      className={`workflow-video-stage ${videoMeta ? "has-video" : ""} ${compact ? "compact" : ""}`}
      ref={stageRef}
      style={style}
    >
      {videoUrl ? (
        <video
          ref={videoRef}
          controls={showControls && !cropOverlay}
          controlsList="nodownload noplaybackrate"
          disablePictureInPicture
          muted
          playsInline
          preload={showControls ? "metadata" : "auto"}
          src={videoUrl}
          onLoadedMetadata={onVideoLoaded}
          onPlay={(event) => {
            if (!showControls && !allowProgrammaticPlayback) {
              event.currentTarget.pause();
            }
          }}
          onClick={(event) => {
            if (!showControls) {
              event.currentTarget.pause();
            }
          }}
          onSeeked={(event) => onVideoTimeChange?.(event.currentTarget.currentTime)}
          onTimeUpdate={(event) => onVideoTimeChange?.(event.currentTarget.currentTime)}
        />
      ) : (
        <div className="upload-empty-state">等待选择视频</div>
      )}
      {cropOverlay && cropRect && onCropPointerDown && onCropPointerMove && onCropPointerUp ? (
        <div
          className="workflow-crop-box"
          style={{
            height: `${cropRect.height * 100}%`,
            left: `${cropRect.x * 100}%`,
            top: `${cropRect.y * 100}%`,
            width: `${cropRect.width * 100}%`,
          }}
          onPointerCancel={onCropPointerUp}
          onPointerDown={(event) => onCropPointerDown(event, "move")}
          onPointerMove={onCropPointerMove}
          onPointerUp={onCropPointerUp}
        >
          {(["nw", "ne", "sw", "se"] as CropDragMode[]).map((mode) => (
            <span
              className={`crop-handle ${mode}`}
              key={mode}
              onPointerDown={(event) => onCropPointerDown(event, mode)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function StatusMessage({ message, status }: { message: string; status: UploadStatus }) {
  if (!message) return null;
  return <p className={`upload-message ${status === "error" ? "error" : ""}`}>{message}</p>;
}

function resizeCrop(rect: CropRect, mode: CropDragMode, deltaX: number, deltaY: number): CropRect {
  const minSize = 0.08;
  if (mode === "move") {
    return clampCrop({
      ...rect,
      x: rect.x + deltaX,
      y: rect.y + deltaY,
    });
  }

  const left = rect.x;
  const top = rect.y;
  const right = rect.x + rect.width;
  const bottom = rect.y + rect.height;

  if (mode === "nw") {
    const nextLeft = clamp(left + deltaX, 0, right - minSize);
    const nextTop = clamp(top + deltaY, 0, bottom - minSize);
    return clampCrop({ x: nextLeft, y: nextTop, width: right - nextLeft, height: bottom - nextTop });
  }

  if (mode === "ne") {
    const nextRight = clamp(right + deltaX, left + minSize, 1);
    const nextTop = clamp(top + deltaY, 0, bottom - minSize);
    return clampCrop({ x: left, y: nextTop, width: nextRight - left, height: bottom - nextTop });
  }

  if (mode === "sw") {
    const nextLeft = clamp(left + deltaX, 0, right - minSize);
    const nextBottom = clamp(bottom + deltaY, top + minSize, 1);
    return clampCrop({ x: nextLeft, y: top, width: right - nextLeft, height: nextBottom - top });
  }

  const nextRight = clamp(right + deltaX, left + minSize, 1);
  const nextBottom = clamp(bottom + deltaY, top + minSize, 1);
  return clampCrop({ x: left, y: top, width: nextRight - left, height: nextBottom - top });
}

function clampCrop(rect: CropRect): CropRect {
  const width = Math.min(1, Math.max(0.05, rect.width));
  const height = Math.min(1, Math.max(0.05, rect.height));
  return {
    width,
    height,
    x: Math.min(1 - width, Math.max(0, rect.x)),
    y: Math.min(1 - height, Math.max(0, rect.y)),
  };
}

function clipStartFromHitFrame(hitFrameTime: number, duration: number): number {
  if (!Number.isFinite(duration) || duration <= 1) return 0;
  return clamp(hitFrameTime - 0.5, 0, Math.max(0, duration - 1));
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function statusLabel(status: UploadStatus, isStartingRun = false): string {
  if (isStartingRun) return "分析中";
  switch (status) {
    case "exporting":
      return "分析中";
    case "uploading":
      return "分析中";
    case "success":
      return "分析中";
    case "error":
      return "需处理";
    default:
      return "待上传";
  }
}

function isVideoFile(file: File): boolean {
  return file.type.startsWith("video/") || /\.(mp4|mov|m4v|webm|avi|mkv)$/i.test(file.name);
}

function validateVideoMeta(meta: VideoMeta): string {
  if (!Number.isFinite(meta.duration) || meta.duration <= 0) {
    return "视频信息读取失败，请换一个视频文件。";
  }

  if (meta.duration > maxSourceVideoDurationSec) {
    return `视频不能超过 2 分钟，当前约 ${formatDuration(meta.duration)}。`;
  }

  const longEdge = Math.max(meta.width, meta.height);
  const shortEdge = Math.min(meta.width, meta.height);
  if (longEdge > max4KLongEdge || shortEdge > max4KShortEdge) {
    return `视频不能超过 4K，当前 ${meta.width} x ${meta.height}。`;
  }

  return "";
}

function inferMimeFromFileName(fileName: string): string {
  const lower = fileName.toLowerCase();
  if (lower.endsWith(".mp4") || lower.endsWith(".m4v")) return "video/mp4";
  if (lower.endsWith(".mov")) return "video/quicktime";
  if (lower.endsWith(".webm")) return "video/webm";
  return "application/octet-stream";
}

function buildUploadFileName(originalName: string, mimeType: string): string {
  return `${baseName(originalName)}-teacher-web-trim.${extensionForMime(mimeType)}`;
}

function baseName(fileName: string): string {
  return fileName.replace(/\.[^.]+$/, "").replace(/[^\w.-]+/g, "-").slice(0, 48) || "video";
}

function extensionForMime(mime: string): string {
  if (mime.includes("mp4")) return "mp4";
  if (mime.includes("quicktime")) return "mov";
  if (mime.includes("webm")) return "webm";
  return "webm";
}

function formatBytes(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
}

function formatDuration(value: number): string {
  if (value < 60) return `${Math.ceil(value)} 秒`;
  const minutes = Math.floor(value / 60);
  const seconds = Math.ceil(value % 60);
  return seconds > 0 ? `${minutes} 分 ${seconds} 秒` : `${minutes} 分钟`;
}

function formatClock(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "0:00.00";
  const minutes = Math.floor(value / 60);
  const seconds = value - minutes * 60;
  return `${minutes}:${seconds.toFixed(2).padStart(5, "0")}`;
}

function isFullFrame(rect: CropRect): boolean {
  return rect.x <= 0.01 && rect.y <= 0.01 && rect.width >= 0.98 && rect.height >= 0.98;
}

function classTitle(link: TeacherLinkInfo): string {
  return link.title || "AI教练课程";
}
